import React from "react";
import { GridTable, GridHead, GridRow, GridCell } from "./ui/GridTable";
import { Badge } from "./ui/Badge";
import { RECIPIENT_STATES, ENGAGEMENT_FILTERS } from "./broadcastEnums";

/**
 * The per-person table behind the Recipients and Engagement tabs.
 *
 * ---------------------------------------------------------------------------
 * ONE ROW PER PERSON, NOT PER CHANNEL
 * ---------------------------------------------------------------------------
 * The delivery ledger the server keeps is per CHANNEL: a push row and an email
 * row for the same human, each with its own outcome. The recipients endpoint
 * merges those rows before we ever see them, and where the two disagree the
 * BETTER outcome wins — a user whose push landed but whose email address is
 * suppressed reads as Delivered, not Suppressed.
 *
 * So this grid must not try to split them back out. There is no per-channel
 * sub-row, no "2 of 3 channels" badge, no expander. The `channel` cell names
 * where the surviving outcome came from and nothing more. Re-deriving the
 * ledger in the UI would mean re-implementing the merge rule in a second place,
 * and the two implementations would answer differently the first time the
 * server's rule changed.
 *
 * ---------------------------------------------------------------------------
 * ABSENCE IS NOT BLANK
 * ---------------------------------------------------------------------------
 * A recipient may genuinely have no email, or no phone, or no engagement event
 * yet. Those cells render an em dash in the faintest text tier, never nothing:
 * a blank cell reads as a rendering bug, an em dash reads as "we don't have
 * one". This is the same rule `Num` enforces for counters, applied to strings.
 *
 * Presentational only — no fetching, no services, no clock. The page owns the
 * data and hands it down already decoded.
 */

/* ── Layout ───────────────────────────────────────────────────────────── */

/**
 * The two column arrangements this grid supports.
 *
 * The name and email tracks are `minmax(0,…fr)` so long values truncate rather
 * than blowing the track out; the rest are fixed. The engagement layout drops
 * `Channel` — on that tab the interesting fact is *when* someone last did
 * something, not which pipe carried it — and gives the timestamp a wider track
 * because a formatted date-time is longer than a one-word channel name.
 */
const LAYOUTS = {
  state: {
    columns: "minmax(0,1.1fr) minmax(0,1.2fr) 130px 110px 100px",
    headers: ["User", "Email", "Phone", "Channel", "Status"],
  },
  engagement: {
    columns: "minmax(0,1.1fr) minmax(0,1.2fr) 130px 110px 150px",
    headers: ["User", "Email", "Phone", "Engagement", "Last event"],
  },
};

/** How many skeleton rows a load paints. Enough to fill the fold, not a page. */
const SKELETON_ROWS = 6;

/* ── Vocabulary ───────────────────────────────────────────────────────── */

/**
 * Outcome value → badge tone.
 *
 * Both tabs' vocabularies live in one map because one component renders both
 * final columns, and because `clicked` and `delivered` earning the same tone is
 * a deliberate statement rather than a coincidence: each is the good end of its
 * own funnel.
 */
const TONE_BY_VALUE = {
  delivered: "ok",
  pending: "warn",
  failed: "bad",
  suppressed: "neut",
  clicked: "ok",
  opened_no_click: "neut",
  no_open: "neut",
};

/**
 * Outcome value → the label the pills already use.
 *
 * Built from the enums rather than restated, so a pill and the badge it filters
 * to cannot end up calling the same state two different things.
 */
const LABEL_BY_VALUE = Object.fromEntries(
  [...RECIPIENT_STATES, ...ENGAGEMENT_FILTERS].map((entry) => [
    entry.id,
    entry.label,
  ])
);

/**
 * Turn an unrecognised wire value into something a human can read:
 * `bounced_hard` → `Bounced hard`.
 *
 * An unknown value must stay VISIBLE. Rendering "" for a state the server added
 * after we shipped would blank the column, which looks like a recipient with no
 * outcome rather than a console that is out of date.
 *
 * @param {unknown} value
 * @returns {string}
 */
const humanize = (value) => {
  const words = String(value ?? "").replace(/_/g, " ").trim();
  if (!words) return "";
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
};

/**
 * Format an event instant as a short date-time, e.g. "4 Aug, 09:30".
 *
 * Returns "" for anything unparseable. `new Date("soon").toLocaleString()` is
 * the literal string "Invalid Date", and printing that in a cell is worse than
 * printing the em dash we already use for "no event yet".
 *
 * @param {string|null|undefined} value - An ISO-ish timestamp.
 * @returns {string}
 */
const formatEventAt = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* ── Pieces ───────────────────────────────────────────────────────────── */

/** U+2014. Not a hyphen, not an en dash — the spec's glyph. */
const EM_DASH = "—";

/**
 * The absent-value placeholder, mirroring the one inside `Num`.
 *
 * Local rather than imported because `Num`'s dash is private to it and carries
 * mono/tabular classes that only make sense on a numeral.
 *
 * @param {Object} props
 * @param {string} [props.className] - Extra classes.
 * @returns {React.ReactElement}
 */
const Dash = ({ className = "" }) => (
  <span aria-label="no data" className={`text-bc-faintest ${className}`}>
    {EM_DASH}
  </span>
);

/**
 * A string cell that degrades to an em dash. `font-mono` for the machine-shaped
 * values (email, phone) so ragged addresses still line up down the column.
 *
 * @param {Object} props
 * @param {string|null|undefined} props.value - The text, or null/"" for absent.
 * @param {boolean} [props.mono=false] - Render in the mono face.
 * @returns {React.ReactElement}
 */
const TextValue = ({ value, mono = false }) =>
  value ? (
    <span className={mono ? "block truncate font-mono" : "block truncate"}>
      {value}
    </span>
  ) : (
    <Dash className={mono ? "font-mono" : ""} />
  );

/**
 * The final column's badge. Unknown values fall back to the neutral tone and
 * the de-underscored word.
 *
 * @param {Object} props
 * @param {string|null|undefined} props.value - A recipient state or engagement value.
 * @returns {React.ReactElement}
 */
const OutcomeBadge = ({ value }) => {
  if (!value) return <Dash />;
  const tone = TONE_BY_VALUE[value] || "neut";
  return <Badge tone={tone}>{LABEL_BY_VALUE[value] || humanize(value)}</Badge>;
};

/**
 * One loading row.
 *
 * The bars are INLINE-BLOCK inside the normal text flow, so the row's height
 * comes from the same line box a real row's text would create. A block-level
 * bar would be a few pixels short and the whole table would jolt upward the
 * moment the data arrived — the exact jump the skeleton exists to prevent.
 *
 * @param {Object} props
 * @param {number} props.cells - How many columns to fill.
 * @returns {React.ReactElement}
 */
const SkeletonRow = ({ cells }) => (
  <GridRow>
    {Array.from({ length: cells }, (unused, index) => (
      <GridCell key={index}>
        <span className="inline-block h-2.5 w-full max-w-[120px] animate-pulse rounded bg-bc-hairline align-middle" />
      </GridCell>
    ))}
  </GridRow>
);

/* ── The grid ─────────────────────────────────────────────────────────── */

/**
 * The recipients table.
 *
 * @param {Object} props
 * @param {Array<{id: string, name: string, email: ?string, phone: ?string, channel: ?string, state: ?string, engagement: ?string, lastEventAt: ?string}>} props.recipients - Decoded rows. One per PERSON.
 * @param {boolean} [props.loading=false] - Paint skeleton rows instead of data.
 * @param {'state'|'engagement'} [props.lastColumn='state'] - Which vocabulary the final columns speak.
 * @param {React.ReactNode} [props.emptyMessage] - Shown when there are no rows and nothing is loading.
 * @param {string} [props.className] - Extra classes for the table element.
 * @returns {React.ReactElement}
 */
export const RecipientsGrid = ({
  recipients,
  loading = false,
  lastColumn = "state",
  emptyMessage = "No recipients.",
  className = "",
}) => {
  const layout = LAYOUTS[lastColumn] || LAYOUTS.state;
  const isEngagement = lastColumn === "engagement";
  const rows = Array.isArray(recipients) ? recipients : [];

  if (!loading && rows.length === 0) {
    return (
      <p className="rounded-[10px] border border-bc-border bg-white px-4 py-8 text-center text-[13px] text-bc-soft">
        {emptyMessage}
      </p>
    );
  }

  return (
    <GridTable columns={layout.columns} label="Recipients" className={className}>
      <GridHead>
        {layout.headers.map((header) => (
          <GridCell key={header} head>
            {header}
          </GridCell>
        ))}
      </GridHead>

      {loading
        ? Array.from({ length: SKELETON_ROWS }, (unused, index) => (
            <SkeletonRow key={index} cells={layout.headers.length} />
          ))
        : rows.map((recipient) => (
            <GridRow key={recipient.id}>
              <GridCell>
                <span className="block truncate font-medium text-bc-text">
                  {recipient.name}
                </span>
              </GridCell>
              <GridCell>
                <TextValue value={recipient.email} mono />
              </GridCell>
              <GridCell>
                <TextValue value={recipient.phone} mono />
              </GridCell>
              {isEngagement ? (
                <>
                  <GridCell>
                    <OutcomeBadge value={recipient.engagement} />
                  </GridCell>
                  <GridCell>
                    <TextValue value={formatEventAt(recipient.lastEventAt)} />
                  </GridCell>
                </>
              ) : (
                <>
                  <GridCell>
                    <TextValue value={humanize(recipient.channel)} />
                  </GridCell>
                  <GridCell>
                    <OutcomeBadge value={recipient.state} />
                  </GridCell>
                </>
              )}
            </GridRow>
          ))}
    </GridTable>
  );
};

export default RecipientsGrid;
